// Why an inbound voicemail recording wouldn't play, in one sentence. Pure; unit-tested in tests/fd-dialer.test.ts.
// The <audio> element only fires a bare 'error' event, so the page re-requests the URL to read the server's reason
// (GET /api/inbound/voicemails/:id/audio answers JSON errors: 503 twilio_not_configured, 404, 502 twilio_error…).

export interface AudioCheck {
  status: number | null;   // HTTP status of the re-request; null when it never got an answer
  body?: string;           // response text for a non-2xx answer
  networkError?: string;   // why the re-request itself failed
}

export function voicemailAudioProblem(c: AudioCheck): string {
  if (c.status === null) {
    return `Couldn't load this voicemail${c.networkError ? ` (${c.networkError})` : ''}. Check your connection and press Try again.`;
  }
  if (c.status >= 200 && c.status < 300) {
    return "The recording downloaded, but this browser couldn't play it. Press Try again; if it keeps failing, tell PJ.";
  }
  let message: string | null = null;
  let error: string | null = null;
  try {
    const parsed = JSON.parse(c.body ?? '') as { message?: unknown; error?: unknown };
    if (typeof parsed?.message === 'string' && parsed.message.trim()) message = parsed.message.trim();
    if (typeof parsed?.error === 'string') error = parsed.error;
  } catch { /* not JSON: fall back to the status */ }
  if (c.status === 401) return 'Your session expired. Sign in again to play voicemails.';
  // TELEPHONY's not-configured text names secrets and an admin tool; setters get one plain sentence instead.
  if (error && /not_configured$/.test(error)) return "Voicemails can't play yet because calling isn't set up. Tell PJ.";
  return message ? `Can't play this voicemail: ${message}` : `Can't play this voicemail (the server answered HTTP ${c.status}).`;
}

/**
 * What the inbound voicemail row offers (fix round 2). TELEPHONY sends `canMarkHandled`: admins, or the holder of the
 * voicemail's task; anyone else gets 403 from POST /:id/handled, so the button is shown only when it would work, and
 * the row says whose it is instead. Fails closed: a missing flag hides the button.
 */
export function voicemailRowActions(v: { handledAt: string | null; handledByName: string | null; canMarkHandled?: boolean | null; assigneeId: number | null }, myId: number): { canMarkHandled: boolean; note: string | null } {
  if (v.handledAt) return { canMarkHandled: false, note: v.handledByName ? `Handled by ${v.handledByName}` : 'Handled' };
  if (v.canMarkHandled === true) return { canMarkHandled: true, note: null };
  if (v.assigneeId === null) return { canMarkHandled: false, note: 'An admin handles this one.' };
  if (v.assigneeId === myId) return { canMarkHandled: false, note: null }; // the server said no even though it is mine: show nothing to press
  return { canMarkHandled: false, note: 'Assigned to a teammate.' };
}
